import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, Loader2, ArrowRight, Plus } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";

const PaymentSuccess = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isRTL = i18n.language === "ar";
  
  const [processing, setProcessing] = useState(true);
  const [success, setSuccess] = useState(false);

  const packageId = searchParams.get("package_id");
  const credits = parseInt(searchParams.get("credits") || "0");

  useEffect(() => {
    if (packageId && credits) {
      addCredits();
    } else {
      setProcessing(false);
    }
  }, [packageId, credits]);

  const addCredits = async () => {
    try {
      const { error } = await supabase.functions.invoke("add-credits", {
        body: { 
          packageId, 
          credits,
        },
      });

      if (error) throw error;

      setSuccess(true);
      toast.success(isRTL ? "تم إضافة الرصيد بنجاح!" : "Credits added successfully!");
    } catch (error: any) {
      console.error("Error adding credits:", error);
      toast.error(isRTL ? "حدث خطأ في إضافة الرصيد" : "Error adding credits");
    } finally {
      setProcessing(false);
    }
  };

  if (processing) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
            <p className="text-lg">
              {isRTL ? "جارٍ معالجة الدفع..." : "Processing payment..."}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" dir={isRTL ? "rtl" : "ltr"}>
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 pt-24 flex items-center justify-center min-h-[80vh]">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            {success ? (
              <>
                <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <CheckCircle className="h-10 w-10 text-green-600" />
                </div>
                <CardTitle className="text-2xl text-green-600">
                  {isRTL ? "تم الدفع بنجاح!" : "Payment Successful!"}
                </CardTitle>
                <CardDescription className="text-lg">
                  {isRTL 
                    ? `تم إضافة ${credits} رصيد إلى حسابك`
                    : `${credits} credits have been added to your account`}
                </CardDescription>
              </>
            ) : (
              <>
                <CardTitle className="text-2xl">
                  {isRTL ? "شكراً لك!" : "Thank you!"}
                </CardTitle>
                <CardDescription>
                  {isRTL ? "يمكنك الآن إضافة محتواك" : "You can now add your content"}
                </CardDescription>
              </>
            )}
          </CardHeader>
          
          <CardContent className="space-y-4">
            <Button 
              className="w-full" 
              size="lg"
              onClick={() => navigate("/submit")}
            >
              <Plus className="h-4 w-4 mr-2" />
              {isRTL ? "أضف محتوى الآن" : "Add Content Now"}
            </Button>
            
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => navigate("/")}
            >
              {isRTL ? "العودة للرئيسية" : "Back to Home"}
              <ArrowRight className={`h-4 w-4 ${isRTL ? "mr-2 rotate-180" : "ml-2"}`} />
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentSuccess;
